import { useCallback, useState } from "react"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { UpdateSystemDocument } from "@hydrologiq/gql-experience/graphql"
import { useSystemInfo, useUpdateSystem, useUpdateSystemInfo } from "../providers/helpers/useSystem"

export interface UseRenameSystemReturn {
  renameSystem: (newName: string) => Promise<void>
  renaming: boolean
  error: string | undefined
}

/**
 * Renames a system via the UpdateSystem mutation and keeps the SystemProvider
 * store in sync (both the typed `system` and the `systemInfo` blob that the
 * headline figures read from).
 */
export function useRenameSystem(systemId: string): UseRenameSystemReturn {
  const [renaming, setRenaming] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const systemInfo = useSystemInfo()
  const updateSystem = useUpdateSystem()
  const updateSystemInfo = useUpdateSystemInfo()

  const [renameSystemMutation] = useMutationWithEndpoint(UpdateSystemDocument, undefined, "primary")

  const renameSystem = useCallback(
    async (newName: string) => {
      const name = newName.trim()
      if (!systemId || !name || name === systemInfo?.name) {
        return
      }

      setRenaming(true)
      setError(undefined)
      try {
        const result = await renameSystemMutation({
          variables: { systemId, name },
        })
        const { errors } = result
        if (errors?.length) {
          throw new Error(errors[0].message)
        }

        // Keep local provider state in sync so the title and figures update
        // without waiting for the next poll/refetch.
        updateSystem({ name })
        if (systemInfo) {
          updateSystemInfo({ ...systemInfo, name })
        }
      } catch (err: any) {
        const message =
          err?.graphQLErrors?.[0]?.message || err?.message || "Failed to rename system"
        setError(message)
        throw new Error(message)
      } finally {
        setRenaming(false)
      }
    },
    [systemId, systemInfo, renameSystemMutation, updateSystem, updateSystemInfo],
  )

  return { renameSystem, renaming, error }
}
